import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { displayError } from '@/lib/error';
import { useClientFilesForFbs, useFbsFilterOptions, useGenerateFbsMutation } from '@/services/fbsService';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, ChevronDown, Loader2, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

const GenerateFbsFormSchema = z.object({
  clientId: z.string().uuid('Please select a client'),
  fileNo: z.string().min(1, 'Please select a file number'),
});

type GenerateFbsFormValues = z.infer<typeof GenerateFbsFormSchema>;

interface GenerateFbsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (message: string) => void;
}

interface ComboboxOption {
  value: string;
  label: string;
  searchText?: string;
}

interface SearchableComboboxProps {
  label: string;
  required?: boolean;
  placeholder: string;
  emptyText: string;
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  error?: string;
}

const SearchableCombobox = ({
  label,
  required,
  placeholder,
  emptyText,
  options,
  value,
  onChange,
  disabled = false,
  isLoading = false,
  error,
}: SearchableComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => (o.searchText ?? o.label).toLowerCase().includes(term));
  }, [options, search]);

  useEffect(() => {
    if (open) {
      setSearch('');
      const id = window.setTimeout(() => inputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  // Close on outside click. Rendered inline (not portaled) so the dropdown
  // scrolls normally inside the Dialog's scroll-lock.
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setSearch('');
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-1.5" ref={containerRef}>
      <label className="p-[2px] flex items-center gap-1 text-sm font-medium">
        {label}
        {required && <span className="text-error align-top">*</span>}
      </label>

      <div className="relative">
        <div
          role="combobox"
          aria-expanded={open}
          tabIndex={disabled || isLoading ? -1 : 0}
          onClick={() => {
            if (disabled || isLoading) return;
            setOpen((prev) => !prev);
          }}
          onKeyDown={(e) => {
            if (disabled || isLoading) return;
            if ((e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') && !open) {
              e.preventDefault();
              setOpen(true);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          className={cn(
            'flex items-center w-full border rounded-md px-3 h-12 bg-white text-sm transition-colors cursor-text',
            'border-bordercolor hover:bg-gray-50 focus:outline-none focus-visible:border-focuscolor',
            open && 'border-focuscolor bg-white',
            error && 'border-error',
            (disabled || isLoading) && 'cursor-not-allowed opacity-60 hover:bg-white'
          )}
        >
          <Search className="h-5 w-5 text-[#969696] mr-2 shrink-0" />
          {open ? (
            <input
              ref={inputRef}
              type="text"
              value={search}
              placeholder={selected ? selected.label : placeholder}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#676767] min-w-0"
            />
          ) : (
            <span className={cn('flex-1 truncate text-left', selected ? 'text-[#0A2240]' : 'text-[#676767]')}>
              {isLoading ? 'Loading...' : selected ? selected.label : placeholder}
            </span>
          )}
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-[#969696] shrink-0 ml-2" />
          ) : (
            <ChevronDown
              className={cn('h-4 w-4 text-gray-400 shrink-0 ml-2 transition-transform', open && 'rotate-180')}
            />
          )}
        </div>

        {open && (
          <ul
            className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-y-auto overscroll-contain rounded-md border border-bordercolor bg-white shadow-lg py-1"
            role="listbox"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-3 text-sm text-center text-[#676767]">{emptyText}</li>
            ) : (
              filtered.map((option) => {
                const isSelected = option.value === value;
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onMouseDown={(e) => {
                        // Keep focus on the input; select on click below.
                        e.preventDefault();
                      }}
                      onClick={() => handleSelect(option.value)}
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm flex items-center gap-2 cursor-pointer',
                        'hover:bg-[#EDF5FF]',
                        isSelected && 'bg-[#EDF5FF] text-primary font-medium'
                      )}
                    >
                      <Check
                        className={cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100 text-primary' : 'opacity-0')}
                      />
                      <span className="truncate">{option.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>

      {error && <p className="text-error text-xs">{error}</p>}
    </div>
  );
};

const GenerateFbsModal = ({ open, onOpenChange, onSuccess }: GenerateFbsModalProps) => {
  const { data: filterOptions, isFetching: clientsLoading } = useFbsFilterOptions();
  const generateMutation = useGenerateFbsMutation();

  const form = useForm<GenerateFbsFormValues>({
    resolver: zodResolver(GenerateFbsFormSchema),
    mode: 'onChange',
    defaultValues: { clientId: '', fileNo: '' },
  });

  const { control, reset, handleSubmit, formState } = form;
  const clientId = useWatch({ control, name: 'clientId' }) as string | null | undefined;
  const resolvedClientId = typeof clientId === 'string' && clientId.length > 0 ? clientId : undefined;

  const { data: clientFiles, isFetching: filesLoading } = useClientFilesForFbs(resolvedClientId);

  const clientOptions = useMemo<ComboboxOption[]>(() => {
    const clients = filterOptions?.clients ?? [];
    return clients.map((c) => {
      const code = c.label2 || c.value;
      const name = c.label1 ?? '';
      const label = name ? `${code} - ${name}` : code;
      return {
        value: c.clientId,
        label,
        searchText: `${code} ${name}`.trim(),
      };
    });
  }, [filterOptions?.clients]);

  const fileOptions = useMemo<ComboboxOption[]>(
    () =>
      (clientFiles?.fileNumbers ?? []).map((fileNo) => ({
        value: fileNo,
        label: fileNo,
        searchText: fileNo,
      })),
    [clientFiles?.fileNumbers]
  );

  useEffect(() => {
    if (open) {
      reset({ clientId: '', fileNo: '' });
    }
  }, [open, reset]);

  useEffect(() => {
    form.setValue('fileNo', '');
  }, [resolvedClientId, form]);

  const onSubmit = (values: GenerateFbsFormValues) => {
    generateMutation.mutate(
      { clientId: values.clientId, fileNo: values.fileNo },
      {
        onSuccess: (data) => {
          toast({ description: data.message });
          onSuccess?.(data.message);
          onOpenChange(false);
        },
        onError: (error) => {
          displayError(error.response?.data);
        },
      }
    );
  };

  const filePlaceholder = !resolvedClientId
    ? 'Select a client first'
    : filesLoading
      ? 'Loading files...'
      : fileOptions.length === 0
        ? 'No file numbers found'
        : 'Search File Number';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[791px] gap-0 p-0 overflow-visible">
        <DialogHeader className="px-8 pt-8 pb-0 space-y-0">
          <DialogTitle className="text-2xl font-semibold tracking-tight text-black">
            Generate Billing Summary
          </DialogTitle>
        </DialogHeader>

        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-8 pt-6 pb-8">
              <Controller
                control={control}
                name="clientId"
                render={({ field, fieldState }) => (
                  <SearchableCombobox
                    label="Client #"
                    required
                    placeholder="Search Client Code"
                    emptyText="No clients found"
                    options={clientOptions}
                    value={field.value ?? ''}
                    onChange={(v) => field.onChange(v)}
                    isLoading={clientsLoading && clientOptions.length === 0}
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="fileNo"
                render={({ field, fieldState }) => (
                  <SearchableCombobox
                    label="File #"
                    required
                    placeholder={filePlaceholder}
                    emptyText="No file numbers found"
                    options={fileOptions}
                    value={field.value ?? ''}
                    onChange={(v) => field.onChange(v)}
                    disabled={!resolvedClientId || filesLoading}
                    isLoading={filesLoading}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>
            <div className="flex justify-end gap-3 px-8 pb-8">
              <Button
                type="button"
                variant="outline"
                className="h-9 min-w-[106px] border-primary text-primary"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="h-9 min-w-[106px] bg-primary hover:bg-[#12375C]"
                disabled={generateMutation.isPending || !formState.isValid}
              >
                {generateMutation.isPending ? 'Generating...' : 'Generate'}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateFbsModal;
